/* eslint-disable class-methods-use-this,max-classes-per-file */
import { v4 as uuidv4 } from 'uuid';
import React, { useEffect, useState } from 'react';
import { Box, Dialog } from '@material-ui/core';
import { Alert } from 'react-bootstrap';
import debug from 'debug';
import { satisfies as semverSatisfies } from 'semver';

import { sleep } from 'utils';
import { createConfirmation } from 'actions/Confirmation';
import { addSuccess } from 'actions/Error';
import { version } from 'external_data';
import { INTEGRATIONS } from 'constants/CAFA';
import { getIsLoggerMsLoggingEnabled } from 'reducers/cafaConfigs';

import * as api from '../InstallerApp/index';

export function getIsLoggingEnabled(): boolean {
  const lsValue = localStorage.getItem(INTEGRATIONS.posConfigurations.loggerMs);
  return lsValue ? JSON.parse(lsValue) : false;
}

export function setIsLoggingEnabled(value: boolean) {
  localStorage.setItem(INTEGRATIONS.posConfigurations.loggerMs, String(value));
}

const deviceID = localStorage.getItem('msLogger-device-id') ?? uuidv4();

// To be replaced with redux action addSuccess once connected to redux
let logSuccess = window.alert;
// To be replaced with redux action createConfirmation once connected to redux
let logError = window.alert;

let current = 0;
let totalQueue = 0;
let openQueue: () => void;
let promise = new Promise(resolve => {
  openQueue = resolve;
});
/*
 * Initial promise resolves when initLoggerMS is ready - this way requests that are queued too soon wait in line and get sent when the MS is actually ready to receive them
 *
 * [❎]→[✅]→[✅]→[✅]→[❌]→[❌]→[❌]     Main branch (var "promise") does not catch errors - one single error stops future logs from executing
 *      ↓   ↓   ↓    ↓   ↓   ↓
 *      ✅   ✅   ✅   🔳  🔳  🔳      Returns the current error with an additional catch handler that's not part of the main chain - this way the caller doesn't see the error
 */
const enqueue = fn => {
  totalQueue += 1;
  // If no errors so far, add this request to the queue and record if it errors
  promise = promise
    .then(() => {
      return fn()
        .catch(fn) // Try again immediately
        .catch(() => sleep(1).then(fn)) // Try again after 1 second
        .catch(() => sleep(5).then(fn)) // Try again after 5 seconds
        .catch(e => {
          logError(
            `This POS is not recording logs. Please try restarting the POS\nIf that doesn't work, contact your IT team.`,
          );
          console.error(e);
          throw e;
        });
    })
    .finally(() => {
      current += 1;
    });
  return promise.catch(() => {});
};

const queueSize = () => totalQueue - current;

/**
 * Generic logger - attaches timestamps and creates files as requested
 * Subclasses need to define:
 * 1) File name to save to
 * 2) A format method that accepts some type of data and stringifies it
 * Subclasses are automatically provided
 * 1) Method to add data of the chosen type to the log
 * 2) Method to export the current log into a file (clearing the log in the process)
 */
abstract class Logger<Type> {
  public _log: string[] = [];

  public filename: string;

  constructor(filename: string) {
    this.filename = filename;
  }

  public format(value: Type): any {
    return value;
  }

  public log(value: Type, meta: any = {}) {
    const isEnabled = getIsLoggingEnabled();
    if (!isEnabled) return;
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const fetch = NetworkLogger.ignoreFetch as typeof window.fetch;
    enqueue(async () =>
      fetch('https://localhost.erply.com:10900/log', {
        method: 'POST',
        body: JSON.stringify({
          loglevel: 'ERROR',
          entry: this.format(value),
          meta: {
            filename: this.filename,
            queueSize: queueSize(),
            application: {
              name: 'POS Brazil',
              endpoint: window.location.href,
              version,
            },
            deviceID,
            ...meta,
          },
        }),
      }),
    );
  }
}

/** Log all click events */
class UIEventLogger extends Logger<MouseEvent | KeyboardEvent> {
  constructor() {
    super('clicks');
    document.addEventListener('click', e => this.log(e));
    document.addEventListener('keydown', e => this.log(e));
  }

  private createCSSPath(node: HTMLElement) {
    // Create path
    const path: HTMLElement[] = [];
    let current: any = node;
    while (current instanceof window.HTMLElement) {
      path.push(current);
      current = current.parentNode;
    }
    path.reverse();

    const out: string[] = [];
    let index = 0;
    // Collect paths by data-testid
    // Formatted like CSS ids, except data-test-id gets appended in parentheses like a function call
    // ex ['#col-2', '#layaways', '#sale(12, {"type": "PREPAYMENT")']
    path.forEach((node, i) => {
      const testID = node.attributes?.getNamedItem('data-testid')?.value;
      const testKey = node.attributes?.getNamedItem('data-test-key')?.value;
      const rest = {};
      Array.from(node.attributes)
        .filter(attr => /data-test-/.test(attr.name))
        .filter(attr => attr.name !== 'data-test-key')
        .forEach(attr => {
          const name = attr.name.replace(/data-test-/, '');
          rest[name] = attr.value;
        });
      if (testID) {
        if (Object.keys(rest).length) {
          if (testKey) {
            // Format with key and other attributes
            out.push(`#${testID}(${testKey}, ${JSON.stringify(rest)})`); // #payment(1992, {"type": "CARD", "sum": "12.00"})
          } else {
            // Format with other attributes
            out.push(`#${testID}(${JSON.stringify(rest)})`); // #input({"type": "text", "style": "default"})
          }
        } else if (testKey) {
          // Format with only key
          out.push(`#${testID}(${testKey})`); // #customer-field(address)
        } else {
          // Format without any attributes
          out.push(`#${testID}()`); // #customer-view()
        }
        index = i + 1;
      }
      // skip elements without data-testid for now. if data-testid elements are present we don't need to log everything in the middle
      // Detailed breakdown fill follow the last found data-testid, or the html root if none were founds
    });
    // From the deepest data-testid found, continue with regular CSS selectors
    // Each segment of the path is the tagname with an index
    // followed by id and/or class selectors if any are available
    // ex ['html', '[1]body', '[0]div.col-1', ...]
    path.slice(index).forEach(node => {
      const { id, className, localName: tag } = node;
      let part = tag;
      // Start with the tagname and index in parent
      // Index put on the left side so that they are
      //   * nicely aligned and easy to read or ignore
      //   * not confused with attribute selectors because they are on opposite sides of the tag name
      //   * not confused with array indexing syntax: input[2] implies the 2'rd child of input, but we are specifying that input is the 2'nd child of whatever came before
      if (node.parentNode) {
        const index = Array.from(node.parentNode.children).indexOf(node);
        if (index === -1) {
          // Has a parent, but is not a child of the parent
          // This seems impossible
          part = `[?]${part}`;
        } else {
          part = `[${index}]${part}`;
        }
      }
      // For input elements, append [value=...] for reference (css attribute syntax)
      if ('value' in node) {
        part += `[value=${(node as any).value}]`;
      }
      // Append css IDs and classes in css syntax
      if (id) part += `#${id}`;
      if (className) part += `.${className.replace(/ /g, '.')}`;
      // done
      out.push(part);
    });
    return out;
  }

  format(event: MouseEvent | KeyboardEvent) {
    const data: any = { type: event.type };

    // Collect data
    if (event instanceof window.KeyboardEvent) {
      data.key = event.key;
    }
    if (event.target instanceof window.HTMLElement) {
      data.innerText = event.target?.innerText;
      data.path = this.createCSSPath(event.target);
    }

    // Trim data
    if (data.innerText?.length > 100) {
      data.innerText = `${data.innerText.slice(0, 100)}⋯`;
    }

    return data;
  }
}

/** Log redux actions (and prepend the state to every file as well) */
class ReduxLogger extends Logger<any> {
  store: any;

  state: any;

  constructor() {
    super('redux');
  }

  setStore(store) {
    this.log({ type: '@@init', payload: store });
  }
}

/** Log network requests and responses (cap at 5000 chars per response) */
class NetworkLogger extends Logger<any> {
  static ignoreFetch = window.fetch;

  constructor() {
    super('network');
    this.patchFetch()
    this.patchXmlHttpRequest()
  }
  private patchFetch() {
    const orig = window.fetch;
    window.fetch = (...params) => {
      const id = uuidv4();
      this.logRequest( id, params );
      const promise = orig.apply(window, params);
      promise.then(
        res => {
          const orig = res?.json;
          // NB: To avoid errors from consuming the body twice we hide
          // the logging in the res.json() function.
          // This means the response is not logged until the caller actually
          // reads response.json(), and if they never do then nothing is logged
          // Potential future improvement: Process the response immediately, create fake json/text/etc methods for accessing the already read contents
          res.json = () => {
            const ret = orig.apply(res, []);
            ret.then(text => this.logResponse( id, text, res.status));
            return ret;
          };
        },
        err => this.logError(id, err.message),
      );
      return promise;
    };
  }

  private patchXmlHttpRequest() {
    const logger = this;
    const orig = XMLHttpRequest.prototype.open
    // @ts-ignore
    XMLHttpRequest.prototype.open = function(method, url, async, user, password) {
      const id = uuidv4();

      logger.logRequest(id, {method, requestURL: url, async, user, password})
      this.addEventListener('load', e => {
        logger.logResponse(id, this.response, this.status)
      })
      this.addEventListener('timeout', e => {
        logger.logError(id, 'Timeout error')
      })
      this.addEventListener('error', e => {
        logger.logError(id, 'Network error')
      })

      return orig.apply(this, [method, url, async, user, password]);
    }
  }

  private logRequest(id: string, request: any) {
    this.log({id, type: 'outgoing', data: request})
  }
  private logResponse(id: string, response: any, status: number) {
    this.log({id, type: 'incoming', data: response, status})
  }
  private logError(id: string, error: any) {
    this.log({id, type: 'error', data: error})
  }
}

/** Log calls to any console methods */
class ConsoleLogger extends Logger<{ type: any; params: any[] }> {
  static ignoreLog = window.console.log.bind(window.console);

  constructor() {
    super('console-log');
    const { console } = window as any;
    Object.keys(console).forEach(k => {
      const fn = console[k];
      console[k] = (...params) => {
        this.log({ type: k, params });
        return fn.apply(console, params);
      };
    });
  }

  format(value): string {
    return value;
  }
}

const initLoggerMs = async (log, rerun = false) => {
  const ENTITY = 'logger-ms';
  /** True if we have made any changes to the installer / MS. In that case this init will run a second time (if it is the second time, then any further discrepancies are errors)  */
  let madeChanges = false;

  /** On the first run, log as warning - On the rerun throw as error */
  const warn = rerun
    ? msg => {
        throw new Error(`${msg}. Please contact your IT team!`);
      }
    : msg => log(`⚠ ${msg}`);

  let info = api.getInfo();
  // const latest = Promise.resolve('v0.0.4');
  const latest = api
    .getIntegrationVersion(ENTITY)
    .then(res => res.data.builds.slice(-1)[0]);

  log('Is the installer running?');
  await info.catch(e => {
    throw new Error(`The installer is not running (${e.message})`);
  });
  log('Does the installer version satisfy the constraint ^2.1.0');
  await info.then(info => {
    if (!semverSatisfies(info.data.version, '^2.1.0')) {
      log('No, attempting to update');
      return api.updateInstaller().then(
        () => {
          log('Update done, reinitializing');
          return initLoggerMs(log);
        },
        e => {
          log('Update failed');
          console.error(e);
          throw new Error(
            `The installer is outdated (detected: ${info.data.version}). Attempts to update the installer failed with error ${e.message}`,
          );
        },
      );
    }
  });

  const installMS = async (
    step: -1 | 0 | 1 | 2,
    entity: string,
    version: string,
    versionToUninstall?: string,
  ) => {
    switch (step) {
      case -1:
        if (!versionToUninstall)
          throw new Error(
            `This should never happen (${entity} outdated, but failed to determine current version)`,
          );
        log(`Stopping ${entity} ${versionToUninstall}...`);
        await api
          .stopIntegrationProcess(entity, versionToUninstall)
          .catch(() => {
            throw new Error(
              `${entity} ${versionToUninstall} is outdated (expected ${version}), but was not able to be stopped`,
            );
          });
      // fallthrough
      case 0:
        if (!versionToUninstall)
          throw new Error(
            `This should never happen (${entity} outdated, but failed to determine current version)`,
          );
        log(`Uninstalling ${entity} ${versionToUninstall}...`);
        await api.removeIntegration(entity, versionToUninstall).catch(() => {
          throw new Error(
            `${entity} ${versionToUninstall} is outdated (expected ${version}), but was not able to be uninstalled`,
          );
        });
      // fallthrough
      case 1: {
        log(`Installing ${entity} ${version}...`);
        // Ignore download errors, maybe it was already downloaded
        const e1 = await api
          .downloadIntegration(entity, version)
          .catch(e1 => e1);
        await api.installIntegration(entity, version).catch(e2 => {
          throw new Error(
            `${entity} ${version} was unable to be downloaded and/or installed (download: ${e1?.message ??
              'no error'}, install:${e2.message})`,
          );
        });
      }
      // fallthrough
      case 2:
        log(`Starting ${entity} ${version}...`);
        await api.startIntegrationProcess(entity, version).catch(() => {
          throw new Error(`${entity} ${version} was unable to start`);
        });
      // fallthrough
      default:
    }
  };

  log('Is the microservice installed and running');
  let msInfo = await info.then(info =>
    info.data.integrations?.find(int => int.name === ENTITY),
  );
  if (msInfo && msInfo.version !== (await latest)) {
    warn('The logger microservice is outdated');
    madeChanges = true;
    await installMS(
      msInfo.status === 'StatusRunning' ? -1 : 0,
      ENTITY,
      await latest,
      msInfo.version,
    );
    info = api.getInfo();
    msInfo = info.then(info =>
      info.data.integrations?.find(int => int.name === ENTITY),
    );
  } else if (!msInfo) {
    warn('The logger microservice is not installed');
    madeChanges = true;
    await installMS(1, ENTITY, await latest);

    info = api.getInfo();
    msInfo = info.then(info =>
      info.data.integrations?.find(int => int.name === ENTITY),
    );
  }

  log('Is the configuration correct? (loglevel=DEBUG)');
  const config = await api
    .getIntegrationConfigValue(ENTITY, await latest)
    .then(res => JSON.parse(res.data.config));
  if (config.logLevel !== 'DEBUG') {
    warn('No, updating');
    madeChanges = true;
    if (msInfo.status === 'StatusRunning') {
      log('Stopping ms for config update');
      await api.stopIntegrationProcess(ENTITY, await latest).catch(() => {
        throw new Error('Logging is not configured.');
      });
      log('updating config');
      await api.saveIntegrationConfig(
        ENTITY,
        await latest,
        JSON.stringify({ ...config, logLevel: 'DEBUG' }, undefined, 4),
      );
      log('Starting the ms back up');
      await api.startIntegrationProcess(ENTITY, await latest).catch(() => {
        throw new Error('The logger microservice is not started.');
      });
    } else {
      log('updating config');
      await api.saveIntegrationConfig(
        ENTITY,
        await latest,
        JSON.stringify({ ...config, loglevel: 'DEBUG' }, undefined, 4),
      );
    }
  }

  log('Is the logger MS running?');
  if (msInfo.status !== 'StatusRunning') {
    warn('The logger microservice is not started');
    madeChanges = true;
    await installMS(2, ENTITY, await latest);
  }

  log('Logger MS should now be ready');
  log('');
  if (madeChanges) {
    log('== Since we had to make some changes, the tests will now rerun ==');
    log('== Any issues found on the second run will count as errors ==');
    log('');
    return initLoggerMs(log, true);
  }
  logSuccess('Logs are being recorded!');
};

const loggers = (() => {
  const uiEventLogger = new UIEventLogger();
  const reduxLogger = new ReduxLogger();
  const networkLogger = new NetworkLogger();
  const consoleLogger = new ConsoleLogger();
  return { uiEventLogger, reduxLogger, networkLogger, consoleLogger };
})();

export const loggingMiddleware = ({ dispatch, getState }) => {
  let isLoggingInitialized;

  function initLogging() {
    logError = (message: string) =>
      new Promise(res =>
        dispatch(
          createConfirmation(res, null, {
            title: 'There was an issue',
            body: message,
          }),
        ),
      );
    logSuccess = (message: string) => dispatch(addSuccess(message));

    // Init ms
    initLoggerMs(debug('pnp-mslog-init'))
      .then()
      .catch(e =>
        logError(
          `Failed to initialize logging: ${e.message}\nPlease contact your IT team`,
        ),
      )
      .finally(openQueue);

    // Hook up redux logger
    loggers.reduxLogger.setStore(getState());
  }

  return next => action => {
    const isEnabledInCafa = getIsLoggerMsLoggingEnabled(getState());
    const isEnabledInLs = getIsLoggingEnabled();

    if (
      typeof isEnabledInCafa === 'boolean' &&
      isEnabledInLs !== isEnabledInCafa
    ) {
      setIsLoggingEnabled(isEnabledInCafa);
    }

    if (isEnabledInCafa || isEnabledInLs) {
      if (!isLoggingInitialized) {
        initLogging();
        isLoggingInitialized = true;
      }
      loggers.reduxLogger.log(action);
    }

    next(action);
  };
};

/** @deprecated Not to be used as of PBIB-3815 */
export const triggerLogUpload = (clientCode: string) => {
  const fetch = NetworkLogger.ignoreFetch as typeof window.fetch;
  console.log('[sendlog] Triggering log upload...');
  return Promise.all([
    fetch(
      `https://localhost.erply.com:10900/api/v1/service/sendlog?clientCode=${clientCode}`,
    ).then(r => r.json()),
    fetch(
      `https://localhost.erply.com:10103/api/v1/service/sendlog?clientCode=${clientCode}`,
    )
      .then(r => r.json())
      .catch(() => {
        console.warn('[sendlog] Cayan MS not installed');
        return { records: [{ statusMessage: '' }] };
      }),
  ]).then(res => {
    console.log('[sendlog] Triggered', res);
    window.alert(res.map(r => r.records[0].statusMessage).join('\n'));
    return res.map(r => r.records[0].statusMessage).join('\n');
  });
};

/** @deprecated */
const LoggerSetup = ({ children }) => {
  const [logs, setLogs] = useState<string[]>([]);
  const [ready, setReady] = useState<undefined | Error | true>();
  const log = (...params) =>
    setLogs(prev => prev.concat(params.map(String).join(' ')));
  useEffect(() => {
    initLoggerMs(log).then(
      () => setReady(true),
      e => setReady(e),
    );
  }, []);

  const [closed, setClosed] = useState(false);
  useEffect(() => {
    if (ready !== true) return () => {};
    const t = setTimeout(() => setClosed(true), 2000);
    return () => clearTimeout(t);
  }, [ready]);

  if (closed) return children;
  return (
    <Dialog open={true} fullScreen>
      <Alert variant="info">Please wait, validating logger-ms...</Alert>
      <Box margin={2}>
        <div>
          {logs.map((log, i) => (
            <div key={i}>
              <span style={{ fontFamily: 'monospace' }}>
                {!!log && (i === logs.length - 1 ? '[  ]' : '[OK]')}
              </span>
              {log}
            </div>
          ))}
        </div>
      </Box>
      <Box margin={2}>
        {ready instanceof Error && (
          <Alert variant="danger">
            <p>{ready.message}</p>
            Please contact your IT team.
          </Alert>
        )}
        {ready === true && (
          <Alert variant="success">Logs are being recorded</Alert>
        )}
      </Box>
    </Dialog>
  );
};

export const workerEnhancer =
  /** Enable the worker to send logs to the LoggingService to be recorded */
  (port: MessagePort) => {
    port.addEventListener('message', e => {
      if (e.data?.type !== 'LoggingService:log') return undefined;

      const {
        payload: { type, source, data },
      } = e.data;

      switch (type) {
        case 'network':
          return loggers.networkLogger.log(data, { source });
        case 'redux':
          return loggers.reduxLogger.log(data, { source });
        case 'uiEvent':
          return loggers.uiEventLogger.log(data, { source });
        case 'console':
          return loggers.consoleLogger.log(data, { source });
        default:
          return undefined;
      }
    });
  };
